/**
 * Telling the server this device is signing out.
 *
 * WHY THIS EXISTS
 * ---------------
 * Logging out used to be entirely client-side: clear localStorage, redirect.
 * The server was never told, so `Login_Table.active_laptop_session` stayed set
 * — and that column is exactly what the login endpoint checks before showing
 * "Already signed in elsewhere". Nothing else ever clears it, so after a user's
 * first sign-in that prompt appeared on every subsequent sign-in, describing
 * their own dead session on the very machine they were sitting at.
 *
 * Called before the tokens are discarded, because the endpoint identifies the
 * session from them.
 */

import baseURL from '@/http';
import { getDeviceType, getDeviceUid } from '@/lib/deviceId';
import { clearTokens } from '@/services/tokenService';

const TOKEN_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';

const read = (key: string): string => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

/**
 * Release this device's session slot. Never throws and never blocks logout.
 *
 * Uses `keepalive` so the request survives the navigation that logout performs
 * immediately afterwards — a plain fetch would be cancelled by the redirect,
 * which is the failure this whole change is about. `sendBeacon` is not an
 * option: it cannot carry the Authorization header the endpoint needs.
 *
 * A failure here is not surfaced. The user is signing out either way, and the
 * server-side slot is reclaimed on their next sign-in from this same browser
 * because the device id identifies it as the slot's owner.
 */
export const releaseServerSession = async (): Promise<void> => {
  const access = read(TOKEN_KEY);
  const refresh = read(REFRESH_KEY);

  // Nothing to identify the session with — the server could not act on it.
  if (!access && !refresh) return;

  try {
    await fetch(`${baseURL}/public/Signout/`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
        'X-Device-Type': getDeviceType(),
        'X-Device-Id': getDeviceUid(),
      },
      body: JSON.stringify({ refresh }),
    });
  } catch (err) {
    console.warn('[sessionSignout] could not release the server session', err);
  }
};

/**
 * Undo a sign-in that got its tokens but never opened a session.
 *
 * Two dialogs sit between "the server issued tokens" and "the user is in": the
 * institute picker and the no-access notice. Backing out of either used to
 * close the dialog and nothing else - the tokens stayed in localStorage (so a
 * reload walked straight into the app on an UNSCOPED token, which merges every
 * institute the account touches) and the account's concurrent-login slot
 * stayed claimed on the server, so the next sign-in from any other device was
 * told the account was "already signed in elsewhere" on a session nobody ever
 * entered.
 *
 * Awaited, not fire-and-forget: there is no navigation racing it here, and the
 * endpoint identifies the session from the very tokens cleared right after.
 */
export const abandonSession = async (): Promise<void> => {
  await releaseServerSession();
  clearTokens();
  try {
    localStorage.removeItem('session_id');
  } catch {
    /* storage unavailable - nothing left to clear */
  }
};
